/**
 * Facts come from the product attribute array, except for the catalog's native
 * `product.model`, which is the established PDP fallback for Model-code.
 */
export type ProductPresentationAttribute = {
  name?: unknown;
  options?: unknown;
};

export type ProductPresentationSource = {
  name?: unknown;
  model?: unknown;
  manufacturer?: { name?: unknown } | null;
  breadcrumbs?: { name?: unknown; url?: unknown }[] | null;
  attributes?: ProductPresentationAttribute[] | null;
};

export type ProductPresentationFact = {
  label: "Model" | "Colour" | "Size" | "Made in";
  value: string;
};

export type ProductDimension = {
  key: "lens" | "bridge" | "temple";
  label: "Lens width" | "Bridge width" | "Temple width";
  value: string;
  questionValue: string;
};

export type ProductMaterialKind = "plastic" | "metal";

export type PrescriptionQuestionBlocks = {
  upper: [string, string];
  lower: [string];
};

export type ProductPresentation = {
  identity: ProductPresentationFact[];
  dimensions: ProductDimension[];
  material: string;
  materialKind: ProductMaterialKind | null;
  rxEligible: boolean;
  questions: string[];
  questionBlocks: PrescriptionQuestionBlocks | null;
};

const QUESTION_ONE =
  "Will you fit prescription lenses into a frame I bought elsewhere, and what do you charge for it?";
const QUESTION_THREE =
  "Do you need me to sign anything about breakage during fitting?";

const MISSING_DIMENSION_VALUES = new Set([
  "-",
  "n/a",
  "na",
  "none",
  "o/s",
  "one size",
]);

const PLASTIC_MATERIALS = new Set([
  "acetate",
  "plastic",
  "propionate",
  "nylon",
  "polyamide",
  "injection",
]);

const METAL_MATERIALS = new Set([
  "metal",
  "titanium",
  "stainless steel",
  "steel",
  "aluminium",
  "aluminum",
  "monel",
  "nickel silver",
  "copper",
  "brass",
  "gold",
  "silver",
]);

/** Strip the catalog's optional trailing colon and compare field names safely. */
export function normalizePresentationAttributeName(value: unknown): string {
  return String(value ?? "")
    .replace(/:\s*$/, "")
    .trim()
    .toLocaleLowerCase("en-US");
}

function values(value: unknown): string[] {
  const source = Array.isArray(value) ? value : [value];
  return source.map((item) => String(item ?? "").trim()).filter(Boolean);
}

function attributeValue(
  attributes: ProductPresentationAttribute[],
  aliases: readonly string[]
): string {
  const accepted = new Set(aliases.map(normalizePresentationAttributeName));
  const attribute = attributes.find((item) =>
    accepted.has(normalizePresentationAttributeName(item?.name))
  );
  return attribute ? values(attribute.options).join(", ") : "";
}

function isMeasurement(value: string): boolean {
  const normalized = value.trim().toLocaleLowerCase("en-US");
  if (!normalized || MISSING_DIMENSION_VALUES.has(normalized)) return false;
  // Catalog dimensions are millimetres. Require a numeric value so a value
  // such as "one size" cannot be relabelled as a lens, bridge, or temple size.
  if (!/^\d+(?:\.\d+)?(?:\s*mm)?$/i.test(normalized)) return false;
  return Number.parseFloat(normalized) > 0;
}

function questionMeasurement(value: string): string {
  const trimmed = value.trim();
  return /\bmm\b/i.test(trimmed) ? trimmed : `${trimmed} mm`;
}

function sizeMeasurement(value: string): string {
  return value
    .trim()
    .replace(/\s*mm\b/i, "")
    .trim();
}

function normalizeExplicitSize(value: string): string {
  const match = value
    .trim()
    .match(
      /^(\d+(?:\.\d+)?)\s*[x×-]\s*(\d+(?:\.\d+)?)\s*[x×-]\s*(\d+(?:\.\d+)?)\s*(?:mm)?$/i
    );
  if (!match) return value;
  return `${match[1]} – ${match[2]} – ${match[3]} mm`;
}

/**
 * Convert only exact, known catalog material names to a broad question term.
 * Unknown material strings remain available as their original text rather
 * than being guessed to be plastic or metal.
 */
export function classifyFrameMaterial(
  material: unknown
): ProductMaterialKind | null {
  const normalized = String(material ?? "")
    .trim()
    .toLocaleLowerCase("en-US");
  if (PLASTIC_MATERIALS.has(normalized)) return "plastic";
  if (METAL_MATERIALS.has(normalized)) return "metal";
  return null;
}

function frameContext(
  product: ProductPresentationSource,
  model: string
): string {
  const name = String(product.name ?? "")
    .replace(/\s+/g, " ")
    .trim();
  if (model) return model;
  if (name) return name;
  return "this";
}

function buildQuestionTwo(
  product: ProductPresentationSource,
  model: string,
  dimensions: ProductDimension[],
  material: string,
  materialKind: ProductMaterialKind | null
): string {
  const byKey = new Map(
    dimensions.map((dimension) => [dimension.key, dimension])
  );
  const lens = byKey.get("lens");
  const bridge = byKey.get("bridge");
  const temple = byKey.get("temple");
  const materialPhrase = materialKind || material;

  if (lens && bridge && temple) {
    const materialPart = materialPhrase ? ` ${materialPhrase}` : "";
    const context = frameContext(product, model);
    const contextPart =
      !materialPhrase && context !== "this" ? ` ${context}` : "";
    return `Does my prescription work in a ${lens.questionValue} lens, ${bridge.questionValue} bridge, ${temple.questionValue} temple${materialPart}${contextPart} frame?`;
  }

  const labelled = [
    lens ? `${lens.questionValue} lens width` : "",
    bridge ? `${bridge.questionValue} bridge width` : "",
    temple ? `${temple.questionValue} temple width` : "",
  ].filter(Boolean);
  const context = frameContext(product, model);
  const frame = materialPhrase
    ? context === "this"
      ? `this ${materialPhrase} frame`
      : `this ${materialPhrase} ${context} frame`
    : context === "this"
    ? "this frame"
    : `this ${context} frame`;

  if (!labelled.length) return `Does my prescription work in ${frame}?`;
  if (labelled.length === 1)
    return `Does my prescription work in ${frame} with a ${labelled[0]}?`;
  if (labelled.length === 2)
    return `Does my prescription work in ${frame} with a ${labelled[0]} and a ${labelled[1]}?`;
  return `Does my prescription work in ${frame} with a ${labelled[0]}, a ${labelled[1]}, and a ${labelled[2]}?`;
}

/**
 * Build PDP-ready product facts and optician questions. Model attributes win;
 * only their absence permits the canonical native `product.model` fallback.
 * RX questions are intentionally exposed only when the exact value is `Yes`.
 */
export function getProductPresentation(
  product: ProductPresentationSource | null | undefined
): ProductPresentation {
  const attributes = Array.isArray(product?.attributes)
    ? product?.attributes || []
    : [];
  const attributeModel = attributeValue(attributes, [
    "Model",
    "Model-code",
    "Model code",
  ]);
  const model = attributeModel || String(product?.model ?? "").trim();
  const colour = attributeValue(attributes, [
    "Color code",
    "Color-code",
    "Colour code",
    "Colour-code",
  ]);
  const madeIn = attributeValue(attributes, ["Made in"]);
  const material = attributeValue(attributes, ["Frame material", "Material"]);

  const dimensions = (
    [
      ["lens", "Lens width", ["Lens width"]],
      ["bridge", "Bridge width", ["Bridge width"]],
      ["temple", "Temple width", ["Temple width", "Frame length"]],
    ] as const
  ).flatMap(([key, label, aliases]) => {
    const value = attributeValue(attributes, aliases);
    if (!isMeasurement(value)) return [];
    return [{ key, label, value, questionValue: questionMeasurement(value) }];
  });

  const explicitSize = attributeValue(attributes, ["Size"]);
  const fallbackSize =
    dimensions.length === 3
      ? `${dimensions
          .map((dimension) => sizeMeasurement(dimension.value))
          .join("-")} mm`
      : "";
  const size = explicitSize
    ? normalizeExplicitSize(explicitSize)
    : fallbackSize;
  const identity = [
    model ? { label: "Model" as const, value: model } : null,
    colour ? { label: "Colour" as const, value: colour } : null,
    size ? { label: "Size" as const, value: size } : null,
    madeIn ? { label: "Made in" as const, value: madeIn } : null,
  ].filter((fact): fact is ProductPresentationFact => Boolean(fact));

  // A positive category is required: sunglasses can also be RX-able. Missing
  // type data must never turn an unknown product into an eyeglasses frame.
  const categoryPaths = (product?.breadcrumbs || []).map((crumb) => {
    try {
      return new URL(
        String(crumb.url || ""),
        "https://hooliganseyewear.com"
      ).pathname
        .replace(/\/+$/, "")
        .toLowerCase();
    } catch {
      return "";
    }
  });
  const isEyeglasses =
    categoryPaths.includes("/eyeglasses") &&
    !categoryPaths.includes("/sunglasses");
  const rxEligible =
    isEyeglasses && attributeValue(attributes, ["RX-able"]) === "Yes";
  const materialKind = classifyFrameMaterial(material);
  if (!rxEligible) {
    return {
      identity,
      dimensions,
      material,
      materialKind,
      rxEligible: false,
      questions: [],
      questionBlocks: null,
    };
  }

  const questionTwo = buildQuestionTwo(
    product || {},
    model,
    dimensions,
    material,
    materialKind
  );
  const questionBlocks: PrescriptionQuestionBlocks = {
    upper: [QUESTION_ONE, questionTwo],
    lower: [QUESTION_THREE],
  };

  return {
    identity,
    dimensions,
    material,
    materialKind,
    rxEligible: true,
    questions: [...questionBlocks.upper, ...questionBlocks.lower],
    questionBlocks,
  };
}
